import { useQueries, useQuery } from '@tanstack/react-query';
import { t } from 'i18next';

import {
  PieceMetadataModel,
  PieceMetadataModelSummary,
  PieceMetadata,
  ActionBase,
  TriggerBase,
} from '@activepieces/pieces-framework';
import {
  Action,
  ActionType,
  SuggestionType,
  Trigger,
  TriggerType,
} from '@activepieces/shared';

import {
  INTERNAL_ERROR_TOAST,
  showErrorToast,
  toast,
} from '../../../components/ui/use-toast';

import { CORE_STEP_METADATA, piecesApi } from './pieces-api';
import {
  PieceSelectorItem,
  StepMetadata,
  StepMetadataWithSuggestions,
  PieceStepMetadataWithSuggestions,
  PieceStepMetadata,
} from './types';

import { pieceSelectorUtils } from '@/app/builder/pieces-selector/piece-selector-utils';
import { deduplicatePiecesByVersion } from '@/lib/utils';
import { usePieceTranslation } from './pieces-translation-hook';

type UsePieceProps = {
  name: string;
  version?: string;
  language?: string;
  enabled?: boolean;
};
type Step = Action | Trigger;

type UseStepsMetadata = {
  steps: Step[];
  enabled?: boolean;
};

type UseStepMetadata = {
  step: Action | Trigger;
  enabled?: boolean;
};

type UsePiecesProps = {
  searchQuery?: string;
  includeHidden?: boolean;
  includeTags?: boolean;
};

type UsePiecesWithSuggestions = {
  type: 'action' | 'trigger';
  enabled?: boolean;
};

type UseMetadataProps = {
  searchQuery?: string;
  enabled?: boolean;
  type: 'action' | 'trigger';
  language?: string;
};

export const piecesHooks = {
  usePiece: ({ name, version, enabled = true }: UsePieceProps) => {
    const { translate } = usePieceTranslation();
    const query = useQuery<PieceMetadataModel, Error>({
      queryKey: ['piece', name, version],
      queryFn: async () => {
        const piece = await piecesApi.get({ name, version });

        return translate(piece);
      },
      staleTime: Infinity,
      enabled,
    });
    return {
      pieceModel: query.data,
      isLoading: query.isLoading,
      isError: query.isError,
      isSuccess: query.isSuccess,
      refetch: query.refetch,
    };
  },
  useStepMetadata: ({ step, enabled = true }: UseStepMetadata) => {
    const { isPieceStep, pieceName, pieceVersion } = getStepPieceInfo(step);

    const pieceQuery = piecesHooks.usePiece({
      name: pieceName!,
      version: pieceVersion,
      enabled: enabled && isPieceStep && !!pieceName,
    });

    const query = useQuery<StepMetadata, Error>({
      ...stepMetadataQueryBuilder(step, pieceQuery.pieceModel),
      enabled: enabled && (!isPieceStep || !!pieceQuery.pieceModel),
    });

    return {
      stepMetadata: query.data,
      isLoading: query.isLoading || pieceQuery.isLoading,
    };
  },
  useStepsMetadata: ({ steps, enabled = true }: UseStepsMetadata) => {
    const { translate } = usePieceTranslation();

    const pieceQueries = useQueries({
      queries: steps.map((step) => {
        const { isPieceStep, pieceName, pieceVersion } = getStepPieceInfo(step);

        return {
          queryKey: ['piece', pieceName, pieceVersion],
          queryFn: async () => {
            const piece = await piecesApi.get({ name: pieceName!, version: pieceVersion });
            return translate(piece);
          },
          staleTime: Infinity,
          enabled: enabled && isPieceStep && !!pieceName,
        };
      }),
    });

    const pieceModels = pieceQueries.map((query) => query.data);

    return useQueries({
      queries: steps.map((step, index) => {
        const { isPieceStep } = getStepPieceInfo(step);
        const pieceModel = pieceModels[index];

        return {
          ...stepMetadataQueryBuilder(step, pieceModel),
          enabled: enabled && (!isPieceStep || !!pieceModel),
        };
      }),
    });
  },
  usePieces: ({
    searchQuery,
    includeHidden = false,
    includeTags = false,
  }: UsePiecesProps) => {
    const { translate } = usePieceTranslation();
    const query = useQuery<PieceMetadataModelSummary[], Error>({
      queryKey: ['pieces', searchQuery, includeHidden],
      queryFn: async () => {
        const pieces = await piecesApi.list({ searchQuery, includeHidden, includeTags });
        const deduplicatedPieces = deduplicatePiecesByVersion(pieces);
        return Promise.all(deduplicatedPieces.map((piece) => translate(piece)));
      },
      staleTime: searchQuery ? 0 : Infinity,
    });
    return {
      pieces: query.data,
      isLoading: query.isLoading,
      isError: query.isError,
      refetch: query.refetch,
    };
  },
  usePiecesWithSuggestions: ({
    type,
    enabled,
  }: UsePiecesWithSuggestions) => {
    const { translate } = usePieceTranslation();
    const query = useQuery<PieceMetadataModelSummary[], Error>({
      queryKey: ['pieces', type],
      queryFn: async () => {
        const suggestionType =
          type === 'action' ? SuggestionType.ACTION : SuggestionType.TRIGGER;

        const pieces = await piecesApi.list({
          searchQuery: undefined,
          suggestionType,
        });

        return Promise.all(pieces.map((piece) => translate(piece)));
      },
      staleTime: Infinity,
      enabled,
    });
    return {
      pieces: query.data,
      isLoading: query.isLoading,
      refetch: query.refetch,
      isSuccess: query.isSuccess,
    };
  },
  useSwaggerPieces: ({ enabled = true }) => {
    const query = useQuery({
      queryKey: ['swagger-pieces'],
      queryFn: () => piecesApi.getSwaggerPieces(),
      staleTime: Infinity,
      enabled,
    });
    return {
      swaggerPieceRefs: query.data,
      isLoading: query.isLoading,
      isSuccess: query.isSuccess,
      refetch: query.refetch,
    };
  },
  useAllStepsMetadata: ({
    searchQuery,
    type,
    enabled = true,
  }: UseMetadataProps) => {
    const {
      pieces,
      isLoading: piecesIsLoading,
      isSuccess: piecesReady,
    } = piecesHooks.usePiecesWithSuggestions({ type, enabled });

    const { swaggerPieceRefs, isLoading: swaggerPiecesIsLoading } =
      piecesHooks.useSwaggerPieces({
        enabled: enabled && type === 'action',
      });

    const { filterTranslatedPieces } = usePieceTranslation();

    const query = useQuery<StepMetadataWithSuggestions[], Error>({
      queryKey: ['pieces-metadata', searchQuery, type],
      queryFn: async () => {
        try {
          if (!pieces) return [];
          const matchingPieces = searchQuery
            ? filterTranslatedPieces(pieces, searchQuery)
            : pieces;
          
          const piecesMetadata = matchingPieces
            .filter(
              (piece) =>
                (type === 'action' && piece.actions > 0) ||
                (type === 'trigger' && piece.triggers > 0)
            )
            .map((piece) => {
              const metadata = piecesApi.mapToMetadata(type, piece);
              const res: StepMetadataWithSuggestions = {
                ...metadata,
                suggestedActions: piece.suggestedActions,
                suggestedTriggers: piece.suggestedTriggers,
              };
              return res;
            });

          switch (type) {
            case 'trigger':
              return [...piecesMetadata];
            case 'action': {
              const swaggerPieces = await Promise.all(
                (swaggerPieceRefs || []).map(async (swaggerPieceRef) => {
                  try {
                    const swaggerPiece = await piecesApi.get({
                      name: swaggerPieceRef.pieceName,
                    });
                    const baseMetadata =
                      pieceSelectorUtils.transformSwaggerPiece(swaggerPiece);
                    const query = searchQuery?.toLowerCase();
                    const pieceNameMatch = baseMetadata.displayName
                      ?.toLowerCase()
                      .includes(query ?? '');
                    const pieceDescriptionMatch = baseMetadata.description
                      ?.toLowerCase()
                      .includes(query ?? '');
                    const filteredActions = Object.values(
                      swaggerPiece.actions
                    ).filter((action) => {
                      if (!query) return true;
                      const name = action.displayName?.toLowerCase();
                      const description = action.description?.toLowerCase();
                      return (
                        name?.includes(query) || description?.includes(query)
                      );
                    });
                    if (filteredActions.length > 0) {
                      return {
                        ...baseMetadata,
                        suggestedActions: filteredActions as ActionBase[],
                      } as PieceStepMetadataWithSuggestions;
                    }
                    if (pieceNameMatch || pieceDescriptionMatch) {
                      return {
                        ...baseMetadata,
                        suggestedActions: Object.values(
                          swaggerPiece.actions
                        ) as ActionBase[],
                      } as PieceStepMetadataWithSuggestions;
                    }
                    return null;
                  } catch (error) {
                    console.error(
                      `Error loading swagger piece ${swaggerPieceRef.pieceName}:`,
                      error
                    );
                    return null;
                  }
                })
              );

              const validSwaggerPieces = swaggerPieces.filter(
                (piece): piece is PieceStepMetadataWithSuggestions =>
                  piece !== null &&
                  !piecesMetadata.some((regularPiece) => {
                    if (regularPiece.type !== ActionType.PIECE) return false;
                    const regularPieceMetadata =
                      regularPiece as PieceStepMetadata;
                    return regularPieceMetadata.pieceName === piece.pieceName;
                  })
              );
              const filtersPrimitive: StepMetadataWithSuggestions[] = [
                CORE_STEP_METADATA[ActionType.CODE],
                CORE_STEP_METADATA[ActionType.LOOP_ON_ITEMS],
                CORE_STEP_METADATA[ActionType.ROUTER],
              ].filter((step) => passSearch(searchQuery, step));

              return [
                ...filtersPrimitive,
                ...piecesMetadata,
                ...validSwaggerPieces,
              ];
            }
          }
        } catch (error) {
          console.error('Error loading pieces:', error);
          toast(INTERNAL_ERROR_TOAST);
          return [];
        }
      },
      enabled: enabled && piecesReady && !swaggerPiecesIsLoading,
      staleTime: searchQuery ? 0 : Infinity,
    });
    return {
      refetch: query.refetch,
      metadata: query.data,
      isLoading: query.isLoading || piecesIsLoading,
    };
  },
  usePieceActionsOrTriggers: ({
    stepMetadata,
  }: {
    stepMetadata?: StepMetadata;
  }) => {
    const { translate } = usePieceTranslation();
    return useQuery<PieceSelectorItem[], Error>({
      queryKey: [
        'pieceMetadata',
        stepMetadata?.type,
        stepMetadata?.displayName,
        stepMetadata?.type === ActionType.PIECE ||
        stepMetadata?.type === TriggerType.PIECE
          ? (stepMetadata as PieceStepMetadata).pieceVersion
          : undefined,
      ],
      queryFn: async (): Promise<PieceSelectorItem[]> => {
        try {
          if (!stepMetadata) {
            return [];
          }
          switch (stepMetadata.type) {
            case TriggerType.PIECE:
            case ActionType.PIECE: {
              const { suggestedTriggers, suggestedActions, type } =
                stepMetadata as PieceStepMetadataWithSuggestions;

              const isTrigger = type === TriggerType.PIECE;
              const suggestions = isTrigger
                ? suggestedTriggers
                : suggestedActions;

              if (suggestions?.length) {
                return suggestions;
              }

              const pieceMetadata = await piecesApi.get({
                name: stepMetadata.pieceName,
                version: (stepMetadata as PieceStepMetadata).pieceVersion,
              });

              const { triggers, actions} = await translate(pieceMetadata);

              return Object.values(
                isTrigger ? triggers : actions
              );
            }
            case ActionType.CODE:
            case ActionType.LOOP_ON_ITEMS:
            case ActionType.ROUTER:
              return getCoreActions(stepMetadata.type);
            default:
              return [];
          }
        } catch (e) {
          console.error(e);
          showErrorToast(e);
          return [];
        }
      },
    });
  },
};

function getStepPieceInfo(step: Step) {
  const isPieceStep =
    step.type === ActionType.PIECE || step.type === TriggerType.PIECE;
  const pieceName = isPieceStep ? step.settings.pieceName : undefined;
  const pieceVersion = isPieceStep ? step.settings.pieceVersion : undefined;
  const customLogoUrl =
    'customLogoUrl' in step ? step.customLogoUrl : undefined;

  return { isPieceStep, pieceName, pieceVersion, customLogoUrl };
}

function stepMetadataQueryBuilder(step: Step, pieceModel?: PieceMetadataModel) {
  const { pieceName, pieceVersion, customLogoUrl } = getStepPieceInfo(step);

  return {
    queryKey: ['piece', step.type, pieceName, pieceVersion, customLogoUrl],
    queryFn: () => piecesApi.getMetadata(step, pieceModel),
    staleTime: Infinity,
  };
}

function passSearch(
  searchQuery: string | undefined,
  data: (typeof CORE_STEP_METADATA)[keyof typeof CORE_STEP_METADATA],
) {
  if (!searchQuery) {
    return true;
  }
  return JSON.stringify({ data })
    .toLowerCase()
    .includes(searchQuery?.toLowerCase());
}

export function getCoreActions(
  type: ActionType.LOOP_ON_ITEMS | ActionType.CODE | ActionType.ROUTER,
): PieceSelectorItem[] {
  switch (type) {
    case ActionType.CODE:
      return [
        {
          name: 'code',
          displayName: t('Custom Javascript Code'),
          description: CORE_STEP_METADATA.CODE.description,
          type: ActionType.CODE as const,
        },
      ];
    case ActionType.LOOP_ON_ITEMS:
      return [
        {
          name: 'loop',
          displayName: t('Loop on Items'),
          description: CORE_STEP_METADATA.LOOP_ON_ITEMS.description,
          type: ActionType.LOOP_ON_ITEMS as const,
        },
      ];

    case ActionType.ROUTER:
      return [
        {
          name: 'router',
          displayName: t('Router'),
          description: t(
            'Split your flow into branches depending on condition(s)',
          ),
          type: ActionType.ROUTER as const,
        },
      ];
  }
}
